import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Router as BrowserRouter,
  useRouteMatch,
  useHistory,
} from 'react-router-dom';
import history from './router/history';
import { RootState } from './store/store';
import { can } from './services/utils';
import Ability from './interfaces/ability';
import './router/guardContext';
import './router/redirectContext';
import './router/abilityContext';
import './router/layoutContext';
import { GuardProvider, useGuard } from './router/guardContext';
import { RedirectProvider, useRedirect } from './router/redirectContext';
import { AbilityProvider, useAbility } from './router/abilityContext';
import { LayoutProvider, useLayout } from './router/layoutContext';
import { ThemeProvider } from './router/themeContext';
import {
  OtherRedirectProvider,
  useOtherRedirect,
} from './router/otherRedirectContext';
import {
  UnauthorizedRedirectProvider,
  useUnauthorizedRedirect,
} from './router/unauthorizedRedirectContext';
import ROUTES, {
  ARTICLES_VIEW,
  CROP_CHANGE_PLAN_CREATE,
  CROP_CHANGE_PLAN_EDIT,
  CROP_CHANGE_PLAN_INDEX,
  CROP_CHANGE_PLAN_VIEW,
  CROP_FERTILIZATION_PLAN,
  CROP_FERTILIZATION_PLAN_CREATE,
  CROP_FERTILIZATION_PLAN_EDIT,
  CROP_FERTILIZATION_PLAN_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
  CROP_FIELDS_MERGE,
  CROP_FIELD_CREATE,
  CROP_FIELD_EDIT,
  CROP_FIELD_HISTORY,
  CROP_FIELD_HISTORY_OVERVIEW,
  CROP_FIELD_HISTORY_VIEW,
  CROP_FIELD_IMPORT,
  CROP_FIELD_SPLIT,
  CROP_FIELD_VIEW,
  CROP_WORK_CREATE,
  CROP_WORK_EDIT,
  CROP_WORK_VIEW,
  CROP_YIELD_ACCOUNTING_CARGO_CREATE,
  CROP_YIELD_ACCOUNTING_CARGO_EDIT,
  CROP_YIELD_ACCOUNTING_CARGO_INDEX,
  CROP_YIELD_ACCOUNTING_CARGO_VIEW,
  CROP_YIELD_ACCOUNTING_INDEX,
  DASHBOARD,
  EQUIPMENT_LIST_CREATE,
  EQUIPMENT_LIST_EDIT,
  EQUIPMENT_LIST_INDEX,
  EQUIPMENT_LIST_VIEW,
  EQUIPMENT_MAINTENANCE_CREATE,
  EQUIPMENT_MAINTENANCE_EDIT,
  EQUIPMENT_MAINTENANCE_INDEX,
  EQUIPMENT_MAINTENANCE_VIEW,
  REPORTING_BIOLOGICAL_FARMING_INDEX,
  REPORTING_VAAD_LIZ_REPORTS,
  REPORTING_VAAD_LIZ_REPORTS_CREATE,
  REPORTING_VAAD_LIZ_REPORTS_UPDATE,
  REPORTING_VAAD_LIZ_REPORTS_VIEW,
  REPORTING_REPORT_JOURNALS_INDEX,
  EVENTS_VIEW,
  FARM_EDIT,
  FARM_USERS_CREATE,
  FARM_USERS_EDIT,
  FARM_USERS_INDEX,
  FARM_USERS_VIEW,
  FARM_VIEW,
  FINANCE_ANALYSIS_INDEX,
  FINANCE_BUDGET_INDEX,
  FINANCE_MODELING_INDEX,
  FORGOT_PASSWORD,
  HOMEPAGE,
  HUMAN_EMPLOYEES_CREATE,
  HUMAN_EMPLOYEES_EDIT,
  HUMAN_EMPLOYEES_INDEX,
  HUMAN_EMPLOYEES_VIEW,
  HUMAN_PARTNERS_CREATE,
  HUMAN_PARTNERS_EDIT,
  HUMAN_PARTNERS_INDEX,
  HUMAN_PARTNERS_VIEW,
  HUMAN_PARTNER_DEPARTMENTS_CREATE,
  HUMAN_PARTNER_DEPARTMENTS_EDIT,
  INTERNAL_ARTICLES_CREATE,
  INTERNAL_ARTICLES_EDIT,
  INTERNAL_ARTICLES_INDEX,
  INTERNAL_ARTICLES_VIEW,
  INTERNAL_AUDIT_LOGS_INDEX,
  INTERNAL_AUDIT_LOGS_VIEW,
  INTERNAL_CLASSIFIERS_INDEX,
  INTERNAL_CLASSIFIER_VALUES,
  INTERNAL_CLASSIFIER_VALUE_CHILD_CREATE,
  INTERNAL_CLASSIFIER_VALUE_CREATE,
  INTERNAL_CLASSIFIER_VALUE_EDIT,
  INTERNAL_CLASSIFIER_VALUE_VIEW,
  INTERNAL_DASHBOARD,
  INTERNAL_DEVELOPMENT_SANDBOX,
  INTERNAL_EMAIL_TEMPLATE_EDIT,
  INTERNAL_EMAIL_TEMPLATE_INDEX,
  INTERNAL_EVENTS_CREATE,
  INTERNAL_EVENTS_EDIT,
  INTERNAL_EVENTS_INDEX,
  INTERNAL_EVENTS_VIEW,
  INTERNAL_LOGIN,
  INTERNAL_ROLES_CREATE,
  INTERNAL_ROLES_EDIT,
  INTERNAL_ROLES_INDEX,
  INTERNAL_ROLES_VIEW,
  INTERNAL_SYSTEM_PROPERTIES_EDIT,
  INTERNAL_SYSTEM_PROPERTIES_INDEX,
  INTERNAL_USERS_CREATE,
  INTERNAL_USERS_EDIT,
  INTERNAL_USERS_INDEX,
  INTERNAL_USERS_VIEW,
  LIVESTOCK_ANIMALS_AND_EVENTS_INDEX,
  LIVESTOCK_BARN_INDEX,
  LIVESTOCK_BARN_VIEW,
  LIVESTOCK_BARN_EDIT,
  LIVESTOCK_FODDER_NEED_INDEX,
  LIVESTOCK_FODDER_NEED_CREATE,
  LIVESTOCK_FODDER_NEED_EDIT,
  LIVESTOCK_FODDER_NEED_VIEW,
  LIVESTOCK_ANIMALS_VIEW,
  LOGIN,
  PROFILE,
  PROFILE_CHANGE_EMAIL,
  PROFILE_CHANGE_PASSWORD,
  PROFILE_CHANGE_PHONE,
  REGISTER,
  RESET_PASSWORD,
  TOKENS_CREATE,
  TOKENS_EDIT,
  TOKENS_INDEX,
  TOKENS_VIEW,
  WAREHOUSE_DEAL_COPY,
  WAREHOUSE_DEAL_CREATE,
  WAREHOUSE_DEAL_EDIT,
  WAREHOUSE_DEAL_VIEW,
  WAREHOUSE_MOVEMENT_INDEX,
  WAREHOUSE_PRODUCTS_COPY,
  WAREHOUSE_PRODUCTS_CREATE,
  WAREHOUSE_PRODUCTS_EDIT,
  WAREHOUSE_PRODUCTS_INDEX,
  WAREHOUSE_PRODUCTS_VIEW,
  WAREHOUSE_WAREHOUSES_CREATE,
  WAREHOUSE_WAREHOUSES_EDIT,
  WAREHOUSE_WAREHOUSES_INDEX,
  WAREHOUSE_WAREHOUSES_VIEW,
  LIVESTOCK_SUMMARY_INDEX,
  LIVESTOCK_SUMMARY_VIEW,
  LIVESTOCK_METABOLISM_VIEW,
  LIVESTOCK_SOMATIC_CELLS_VIEW,
  LIVESTOCK_REPRODUCTION_VIEW,
  DOCUMENT_STORAGE_INDEX,
  LIVESTOCK_ANIMALS_GRAZING_CREATE,
  LIVESTOCK_ANIMALS_GRAZING_VIEW,
  LIVESTOCK_ANIMALS_GRAZING_EDIT,
  LIVESTOCK_ANIMALS_OTHER_CREATE,
  LIVESTOCK_ANIMALS_OTHER_EDIT,
  LIVESTOCK_ANIMALS_OTHER_VIEW,
  LIVESTOCK_ANIMALS_PREGNANCY_CHECK_CREATE,
  LIVESTOCK_ANIMALS_PREGNANCY_CHECK_EDIT,
  LIVESTOCK_ANIMALS_PREGNANCY_CHECK_VIEW,
  LIVESTOCK_ANIMALS_SEPARATION_CREATE,
  LIVESTOCK_ANIMALS_SEPARATION_EDIT,
  LIVESTOCK_ANIMALS_SEPARATION_VIEW,
  LIVESTOCK_ANIMALS_BREEDING_CREATE,
  LIVESTOCK_ANIMALS_BREEDING_EDIT,
  LIVESTOCK_ANIMALS_BREEDING_VIEW,
  LIVESTOCK_ANIMALS_WEIGHING_CREATE,
  LIVESTOCK_ANIMALS_WEIGHING_EDIT,
  LIVESTOCK_ANIMALS_WEIGHING_VIEW,
  LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_CREATE,
  LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_EDIT,
  LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_VIEW,
} from './router/routes';
import InternalDashboard from './components/internal/dashboard';
import InternalLogin from './pages/internal/login';
import InternalSidebar from './components/internal/sidebar';
import InternalTopbar from './components/internal/topbar';
import IndexAuditLogs from './pages/internal/audit-logs/index';
import ViewAuditLog from './pages/internal/audit-logs/view';
import Layout from './components/layout';
import MetaTagSetter from './components/metatag-setter';
import IndexUser from './pages/internal/users';
import CreateUser from './pages/internal/users/create-user';
import EditUser from './pages/internal/users/edit-user';
import ViewUser from './pages/internal/users/view-user';
import IndexRole from './pages/internal/roles';
import CreateRole from './pages/internal/roles/create-role';
import EditRole from './pages/internal/roles/edit-role';
import ViewRole from './pages/internal/roles/view-role';
import EditSystemConfiguration from './pages/internal/system-configurations/edit-system-configuration';
import IndexSystemConfiguration from './pages/internal/system-configurations/index';
import Homepage from './pages/external/homepage';
import Register from './pages/external/register';
import Login from './pages/external/login';
import ForgotPassword from './pages/external/forgot-password';
import ResetPassword from './pages/external/reset-password';
import Classifiers from './pages/internal/classifiers/classifiers';
import ClassifierValues from './pages/internal/classifiers/classifier-values';
import CreateClassifierValue from './pages/internal/classifiers/create-classifier-value';
import EditClassifierValue from './pages/internal/classifiers/edit-classifier-value';
import Dashboard from './pages/dashboard';
import Sidebar from './components/sidebar';
import Topbar from './components/topbar';
import FieldsAndWorks from './pages/crop/fields-and-works';
import YieldAccounting from './pages/crop/yield-accounting';
import FieldHistory from './pages/crop/field-history';
import CropChangePlan from './pages/crop/crop-change-plan';
import CropChangePlanCreate from './pages/crop/crop-change-plan/create';
import CropChangePlanView from './pages/crop/crop-change-plan/view';
import CropChangePlanEdit from './pages/crop/crop-change-plan/edit';
import FertilizationPlan from './pages/crop/fertilization-plan';
import Movement from './pages/warehouse/movement';
import Products from './pages/warehouse/products';
import ProductsView from './pages/warehouse/products/view';
import ProductsCreate from './pages/warehouse/products/create';
import ProductsEdit from './pages/warehouse/products/edit';
import Warehouses from './pages/warehouse/warehouses';
import WarehouseCreate from './pages/warehouse/warehouses/create';
import WarehouseView from './pages/warehouse/warehouses/view';
import WarehouseEdit from './pages/warehouse/warehouses/edit';
import Employees from './pages/farm/humans/employees';
import CreateEmployee from './pages/farm/humans/employees/create-employee';
import ViewEmployee from './pages/farm/humans/employees/view-employee';
import EditEmployee from './pages/farm/humans/employees/edit-employee';
import CreatePartner from './pages/farm/humans/partners/create-partner';
import ViewPartner from './pages/farm/humans/partners/view-partner';
import EditPartner from './pages/farm/humans/partners/edit-partner';
import CreatePartnerDepartment from './pages/farm/humans/partners/create-department';
import EditPartnerDepartment from './pages/farm/humans/partners/edit-department';
import Partners from './pages/farm/humans/partners';
import EquipmentList from './pages/equipment/list';
import MaintenanceAndRepairs from './pages/equipment/maintenance';
import CreateMaintenance from './pages/equipment/maintenance/create';
import EditMaintenance from './pages/equipment/maintenance/edit';
import ViewMaintenance from './pages/equipment/maintenance/view';
import InitialProfileForm from './pages/profile';
import ChangeEmailForm from './pages/profile/change-email-form';
import ChangePasswordForm from './pages/profile/change-password-form';
import ChangePhoneForm from './pages/profile/change-phone-form';
import DevelopmentSandbox from './pages/sandbox';
import VisibleInDevelopment from './components/development/visible-in-development';
import IndexEmailTemplate from './pages/internal/email-templates';
import EditEmailTemplate from './pages/internal/email-templates/edit-email-template';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/error-fallback';
import FarmUsers from './pages/farm/users';
import FarmCreateUser from './pages/farm/users/create';
import FarmViewUser from './pages/farm/users/view';
import FarmEditUser from './pages/farm/users/edit';
import FarmView from './pages/farm/view';
import FarmEdit from './pages/farm/edit';
import ViewClassifierValue from './pages/internal/classifiers/view-classifier-value';
import DealCreate from './pages/warehouse/movement/deals/create';
import DealtEdit from './pages/warehouse/movement/deals/edit';
import DealtView from './pages/warehouse/movement/deals/view';
import EquipmentCreate from './pages/equipment/list/create';
import EquipmentView from './pages/equipment/list/view';
import EquipmentEdit from './pages/equipment/list/edit';
import ViewField from './pages/crop/fields-and-works/fields/view';
import EditField from './pages/crop/fields-and-works/fields/basic-info/edit';
import MergeField from './pages/crop/fields-and-works/fields/merge';
import SplitField from './pages/crop/fields-and-works/fields/split';
import CreateField from './pages/crop/fields-and-works/fields/basic-info/create';
import CreateWork from './pages/crop/fields-and-works/works/create';
import ViewWork from './pages/crop/fields-and-works/works/view';
import EditWork from './pages/crop/fields-and-works/works/edit';
import ProductionModeling from './pages/finance/production-modeling';
import ProductionCostAnalysis from './pages/finance/production-cost-analysis';
import Budget from './pages/finance/budget';
import ApiTokens from './pages/tokens';
import ApiTokenCreate from './pages/tokens/create';
import ApiTokenView from './pages/tokens/view';
import ApiTokenEdit from './pages/tokens/edit';
import YieldAccountingCargoIndex from './pages/crop/yield-accounting/cargo';
import YieldAccountingCargoCreate from './pages/crop/yield-accounting/cargo/create';
import YieldAccountingCargoView from './pages/crop/yield-accounting/cargo/view';
import ShowArticle from './pages/dashboard/articles/show-article';
import ShowEvent from './pages/dashboard/calendar/show-event';
import Articles from './pages/internal/articles';
import CreateArticle from './pages/internal/articles/create';
import ViewArticle from './pages/internal/articles/view';
import EditArticle from './pages/internal/articles/edit';
import Events from './pages/internal/events';
import CreateEvent from './pages/internal/events/create';
import ViewEvent from './pages/internal/events/view';
import EditEvent from './pages/internal/events/edit';
import AnimalsAndEvents from './pages/livestock/animals-and-events';
import AnimalView from './pages/livestock/animals-and-events/animals/view';
import FodderNeeds from './pages/livestock/fodder-need';
import CreateFodderNeed from './pages/livestock/fodder-need/create';
import EditFodderNeed from './pages/livestock/fodder-need/edit';
import ViewFodderNeed from './pages/livestock/fodder-need/view';
import ReportJournalsList from './pages/reports/report-journals';
import VaadLizReportV2List from './pages/reports/vaad-liz-reports_v2';
import EditReport from './pages/reports/vaad-liz-reports_v2/edit';
import ViewReport from './pages/reports/vaad-liz-reports_v2/view';
import _ from 'lodash';
import i18n from 'i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import FieldHistoryView from './pages/crop/field-history/history/view';
import FertilizationPlanItem, {
  FertilizationPlanFormMode,
} from './pages/crop/fertilization-plan/plan/item';
import Barns from './pages/livestock/barn';
import ViewBarn from './pages/livestock/barn/view';
import BarnEdit from './pages/livestock/barn/edit';
import { BackButtonProvider } from './pages/crop/fields-and-works/backButtonContext';
import CropFieldsFilterContainer from './pages/crop/fields-and-works/crop-fields-filter-container';
import SynchronizeFieldsWithLad from './pages/crop/fields-and-works/fields/basic-info/synchronize';
import CreateReport from './pages/reports/vaad-liz-reports_v2/create';
import BiologicalFarmingReport from './pages/reports/biological-farming';
import LivestockSummary from './pages/livestock/livestock-summary';
import ViewSummary from './pages/livestock/livestock-summary/view';
import ViewMetabolism from './pages/livestock/livestock-summary/metabolism-view';
import ViewSomaticCells from './pages/livestock/livestock-summary/somatic-view';
import ViewReproduction from './pages/livestock/livestock-summary/reproduction-view';
import { WarehouseProvider } from './pages/warehouse/movement/deals/warehouse-context';
import DocumentStorage from './pages/document-storage';
import AnimalGrazing from './pages/livestock/animals-and-events/events/grazing';
import EditGrazing from './pages/livestock/animals-and-events/events/grazing/edit';
import ShowGrazing from './pages/livestock/animals-and-events/events/grazing/show';
import CreateOther from './pages/livestock/animals-and-events/events/other';
import ShowOther from './pages/livestock/animals-and-events/events/other/show';
import EditOther from './pages/livestock/animals-and-events/events/other/edit';
import CreatePregnancy from './pages/livestock/animals-and-events/events/pregnancy';
import ShowPregnancy from './pages/livestock/animals-and-events/events/pregnancy/show';
import EditPregnancy from './pages/livestock/animals-and-events/events/pregnancy/edit';
import CreateSeparation from './pages/livestock/animals-and-events/events/separation';
import EditSeparation from './pages/livestock/animals-and-events/events/separation/edit';
import ShowSeparation from './pages/livestock/animals-and-events/events/separation/show';
import CreateBreeding from './pages/livestock/animals-and-events/events/breeding';
import EditBreeding from './pages/livestock/animals-and-events/events/breeding/edit';
import ShowBreeding from './pages/livestock/animals-and-events/events/breeding/show';
import CreateWeighing from './pages/livestock/animals-and-events/events/weighing';
import EditWeighing from './pages/livestock/animals-and-events/events/weighing/edit';
import ShowWeighing from './pages/livestock/animals-and-events/events/weighing/show';
import CreateStarterFeed from './pages/livestock/animals-and-events/events/starter_feed/create';
import EditStarterFeed from './pages/livestock/animals-and-events/events/starter_feed/edit';
import ShowStarterFeed from './pages/livestock/animals-and-events/events/starter_feed/show';

export default function Router() {
  return (
    // prettier-ignore
    <BrowserRouter history={history}>

      {/* External routes */}
      <MetaTagSetter>
        <Route exact={true} path={HOMEPAGE}><Homepage /></Route>
      </MetaTagSetter>

      <GuardProvider guard={'api'}>
        <RedirectProvider redirect={DASHBOARD}>
          <OtherRedirectProvider otherRedirect={HOMEPAGE}>
            <GuestRoute path={LOGIN}><Login /></GuestRoute>
            <GuestRoute path={REGISTER}><Register /></GuestRoute>
            <GuestRoute path={FORGOT_PASSWORD}><ForgotPassword /></GuestRoute>
            <GuestRoute path={RESET_PASSWORD}><ResetPassword /></GuestRoute>
          </OtherRedirectProvider>
        </RedirectProvider>
      </GuardProvider>

      <LayoutProvider
        layout={({ children }: { children: ReactNode }) => (
          <Layout
            sidebar={<Sidebar />}
            topbar={<Topbar />}
            children={children}
            className="external-theme"
          />
        )}
      >
        <GuardProvider guard={'api'}>
          <RedirectProvider redirect={LOGIN}>
            <AuthRoute path={DASHBOARD}><Dashboard /></AuthRoute>
            <AuthRoute path={ARTICLES_VIEW}><ShowArticle /></AuthRoute>
            <AuthRoute path={EVENTS_VIEW}><ShowEvent /></AuthRoute>

            <AuthRoute path={TOKENS_INDEX}><ApiTokens /></AuthRoute>
            <AuthRoute path={TOKENS_CREATE}><ApiTokenCreate /></AuthRoute>
            <AuthRoute path={TOKENS_VIEW}><ApiTokenView /></AuthRoute>
            <AuthRoute path={TOKENS_EDIT}><ApiTokenEdit /></AuthRoute>

            <AuthRoute exact path={PROFILE}><InitialProfileForm /></AuthRoute>
            <AuthRoute exact path={PROFILE_CHANGE_EMAIL}><ChangeEmailForm /></AuthRoute>
            <AuthRoute exact path={PROFILE_CHANGE_PASSWORD}><ChangePasswordForm /></AuthRoute>
            <AuthRoute exact path={PROFILE_CHANGE_PHONE}><ChangePhoneForm /></AuthRoute>

            <AuthRoute path={FARM_VIEW}><FarmView /></AuthRoute>
            <AuthRoute path={FARM_EDIT}><FarmEdit /></AuthRoute>

            <UnauthorizedRedirectProvider unauthorizedRedirect={HOMEPAGE}>
              <AbilityProvider ability={{ entity_type: 'farm_user' }}>
                <AuthRoute path={FARM_USERS_INDEX}><FarmUsers /></AuthRoute>
                <AuthRoute path={FARM_USERS_CREATE}><FarmCreateUser /></AuthRoute>
                <AuthRoute path={FARM_USERS_VIEW}><FarmViewUser /></AuthRoute>
                <AuthRoute path={FARM_USERS_EDIT}><FarmEditUser /></AuthRoute>
              </AbilityProvider>

              <ThemeProvider theme="crop-theme">
                <AbilityProvider ability={{ entity_type: 'field' }}>
                  <BackButtonProvider>
                    <CropFieldsFilterContainer>
                      <AbilityProvider ability={{ title: 'viewAny field' }}>
                        <AuthRoute path={CROP_FIELDS_AND_WORKS_INDEX}><FieldsAndWorks /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'view field' }}>
                        <AuthRoute path={CROP_FIELD_VIEW}><ViewField /></AuthRoute>
                        <AuthRoute path={CROP_FIELD_HISTORY}><FieldHistory /></AuthRoute>
                        <AuthRoute path={CROP_FIELD_HISTORY_VIEW}><FieldHistoryView /></AuthRoute>
                        <AuthRoute path={CROP_FIELD_HISTORY_OVERVIEW}><FieldHistory /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'create field' }}>
                        <AuthRoute path={CROP_FIELD_CREATE}><CreateField /></AuthRoute>
                        <AuthRoute path={CROP_FIELD_IMPORT}><SynchronizeFieldsWithLad /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'update field' }}>
                        <AuthRoute path={CROP_FIELD_EDIT}><EditField /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'merge field' }}>
                        <AuthRoute path={CROP_FIELDS_MERGE}><MergeField /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'split field' }}>
                        <AuthRoute path={CROP_FIELD_SPLIT}><SplitField /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'view work' }}>
                        <AuthRoute path={CROP_WORK_VIEW}><ViewWork /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'create work' }}>
                        <AuthRoute path={CROP_WORK_CREATE}><CreateWork /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'update work' }}>
                        <AuthRoute path={CROP_WORK_EDIT}><EditWork /></AuthRoute>
                      </AbilityProvider>
                    </CropFieldsFilterContainer>

                    <AbilityProvider ability={{ title: 'viewAny yield_accounting' }}>
                      <AuthRoute path={CROP_YIELD_ACCOUNTING_INDEX}><YieldAccounting /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'viewAny cargo' }}>
                      <AuthRoute path={CROP_YIELD_ACCOUNTING_CARGO_INDEX}><YieldAccountingCargoIndex /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'view cargo' }}>
                      <AuthRoute path={CROP_YIELD_ACCOUNTING_CARGO_VIEW}><YieldAccountingCargoView /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'create cargo' }}>
                      <AuthRoute path={CROP_YIELD_ACCOUNTING_CARGO_CREATE}><YieldAccountingCargoCreate /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'update cargo' }}>
                      <AuthRoute path={CROP_YIELD_ACCOUNTING_CARGO_EDIT}><YieldAccountingCargoCreate /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'viewAny crop_change_plan' }}>
                      <AuthRoute path={CROP_CHANGE_PLAN_INDEX}><CropChangePlan /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'view crop_change_plan' }}>
                      <AuthRoute path={CROP_CHANGE_PLAN_VIEW}><CropChangePlanView /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'create crop_change_plan' }}>
                      <AuthRoute path={CROP_CHANGE_PLAN_CREATE}><CropChangePlanCreate /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'update crop_change_plan' }}>
                      <AuthRoute path={CROP_CHANGE_PLAN_EDIT}><CropChangePlanEdit /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'viewAny fertilization_plan' }}>
                      <AuthRoute path={CROP_FERTILIZATION_PLAN}>
                        <FertilizationPlan />
                      </AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'view fertilization_plan' }}>
                      <AuthRoute path={CROP_FERTILIZATION_PLAN_VIEW}>
                        <FertilizationPlanItem mode={FertilizationPlanFormMode.VIEW} />
                      </AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'create fertilization_plan' }}>
                      <AuthRoute path={CROP_FERTILIZATION_PLAN_CREATE}>
                        <FertilizationPlanItem mode={FertilizationPlanFormMode.CREATE} />
                      </AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'update fertilization_plan' }}>
                      <AuthRoute path={CROP_FERTILIZATION_PLAN_EDIT}>
                        <FertilizationPlanItem mode={FertilizationPlanFormMode.EDIT} />
                      </AuthRoute>
                    </AbilityProvider>
                  </BackButtonProvider>
                </AbilityProvider>
              </ThemeProvider>

              <ThemeProvider theme="livestock-theme">
                <AbilityProvider ability={{ entity_type: 'animal' }}>
                  <AbilityProvider ability={{ title: 'viewAny animal' }}>
                    <AuthRoute path={LIVESTOCK_ANIMALS_AND_EVENTS_INDEX}><AnimalsAndEvents /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view animal' }}>
                    <AuthRoute path={LIVESTOCK_ANIMALS_VIEW}><AnimalView /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'animal_event' }}>
                  <AbilityProvider ability={{ title: 'view animal_event' }}>
                    <AuthRoute path={LIVESTOCK_ANIMALS_GRAZING_VIEW}>
                      <ShowGrazing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_OTHER_VIEW}>
                      <ShowOther />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_PREGNANCY_CHECK_VIEW}>
                      <ShowPregnancy />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_SEPARATION_VIEW}>
                      <ShowSeparation />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_BREEDING_VIEW}>
                      <ShowBreeding />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_WEIGHING_VIEW}>
                      <ShowWeighing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_VIEW}>
                      <ShowStarterFeed/>
                    </AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'create animal_event' }}>
                    <AuthRoute path={LIVESTOCK_ANIMALS_GRAZING_CREATE}>
                      <AnimalGrazing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_OTHER_CREATE}>
                      <CreateOther />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_PREGNANCY_CHECK_CREATE}>
                      <CreatePregnancy />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_SEPARATION_CREATE}>
                      <CreateSeparation />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_BREEDING_CREATE}>
                      <CreateBreeding />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_WEIGHING_CREATE}>
                      <CreateWeighing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_CREATE}>
                      <CreateStarterFeed />
                    </AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update animal_event' }}>
                    <AuthRoute path={LIVESTOCK_ANIMALS_GRAZING_EDIT}>
                      <EditGrazing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_OTHER_EDIT}>
                      <EditOther />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_PREGNANCY_CHECK_EDIT}>
                      <EditPregnancy />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_SEPARATION_EDIT}>
                      <EditSeparation />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_BREEDING_EDIT}>
                      <EditBreeding />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMALS_WEIGHING_EDIT}>
                      <EditWeighing />
                    </AuthRoute>
                    <AuthRoute path={LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_EDIT}>
                      <EditStarterFeed />
                    </AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>

                <AbilityProvider ability={{ entity_type: 'fodder_need' }}>
                  <AbilityProvider ability={{ title: 'viewAny fodder_need' }}>
                    <AuthRoute path={LIVESTOCK_FODDER_NEED_INDEX}><FodderNeeds /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'create fodder_need' }}>
                    <AuthRoute path={LIVESTOCK_FODDER_NEED_CREATE}><CreateFodderNeed /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update fodder_need' }}>
                    <AuthRoute path={LIVESTOCK_FODDER_NEED_EDIT}><EditFodderNeed /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view fodder_need' }}>
                    <AuthRoute path={LIVESTOCK_FODDER_NEED_VIEW}><ViewFodderNeed /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'livestock_summary' }}>
                  <AbilityProvider ability={{ title: 'viewAny livestock_summary' }}>
                    <AuthRoute path={LIVESTOCK_SUMMARY_INDEX}><LivestockSummary /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view livestock_summary' }}>
                    <AuthRoute path={LIVESTOCK_SUMMARY_VIEW}><ViewSummary /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'livestock_metabolism' }}>
                  <AbilityProvider ability={{ title: 'view livestock_metabolism' }}>
                    <AuthRoute path={LIVESTOCK_METABOLISM_VIEW}><ViewMetabolism /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'livestock_somatic_cell' }}>
                  <AbilityProvider ability={{ title: 'view livestock_somatic_cell' }}>
                    <AuthRoute path={LIVESTOCK_SOMATIC_CELLS_VIEW}><ViewSomaticCells /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'livestock_reproduction' }}>
                  <AbilityProvider ability={{ title: 'view livestock_reproduction' }}>
                    <AuthRoute path={LIVESTOCK_REPRODUCTION_VIEW}><ViewReproduction /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
                <AbilityProvider ability={{ entity_type: 'barn' }}>
                  <AbilityProvider ability={{ title: 'viewAny barn' }}>
                    <AuthRoute path={LIVESTOCK_BARN_INDEX}><Barns /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view barn' }} >
                    <AuthRoute path={LIVESTOCK_BARN_VIEW}><ViewBarn /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update barn' }} >
                    <AuthRoute path={LIVESTOCK_BARN_EDIT}><BarnEdit /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
              </ThemeProvider>

              <ThemeProvider theme="warehouse-theme">
                <WarehouseProvider>
                  <AbilityProvider ability={{ entity_type: 'deal' }}>
                      <AbilityProvider ability={{ title: 'viewAny deal' }}>
                        <AuthRoute path={WAREHOUSE_MOVEMENT_INDEX}><Movement /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'view deal' }}>
                        <AuthRoute path={WAREHOUSE_DEAL_VIEW}><DealtView /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'create deal' }}>
                        <AuthRoute path={WAREHOUSE_DEAL_CREATE}><DealCreate /></AuthRoute>
                        <AuthRoute path={WAREHOUSE_DEAL_COPY}><DealCreate /></AuthRoute>
                      </AbilityProvider>
                      <AbilityProvider ability={{ title: 'update deal' }}>
                        <AuthRoute path={WAREHOUSE_DEAL_EDIT}><DealtEdit /></AuthRoute>
                      </AbilityProvider>
                  </AbilityProvider>

                  <AbilityProvider ability={{ entity_type: 'product' }}>
                    <AbilityProvider ability={{ title: 'viewAny product' }}>
                      <AuthRoute path={WAREHOUSE_PRODUCTS_INDEX}><Products /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'view product' }}>
                      <AuthRoute path={WAREHOUSE_PRODUCTS_VIEW}><ProductsView /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'create product' }}>
                      <AuthRoute path={WAREHOUSE_PRODUCTS_CREATE}><ProductsCreate /></AuthRoute>
                      <AuthRoute path={WAREHOUSE_PRODUCTS_COPY}><ProductsCreate /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'update product' }}>
                      <AuthRoute path={WAREHOUSE_PRODUCTS_EDIT}><ProductsEdit /></AuthRoute>
                    </AbilityProvider>
                  </AbilityProvider>

                  <AbilityProvider ability={{ entity_type: 'warehouse' }}>
                    <AbilityProvider ability={{ title: 'viewAny warehouse' }}>
                      <AuthRoute path={WAREHOUSE_WAREHOUSES_INDEX}><Warehouses /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'view warehouse' }}>
                      <AuthRoute path={WAREHOUSE_WAREHOUSES_VIEW}><WarehouseView /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'create warehouse' }}>
                      <AuthRoute path={WAREHOUSE_WAREHOUSES_CREATE}><WarehouseCreate /></AuthRoute>
                    </AbilityProvider>
                    <AbilityProvider ability={{ title: 'update warehouse' }}>
                      <AuthRoute path={WAREHOUSE_WAREHOUSES_EDIT}><WarehouseEdit /></AuthRoute>
                    </AbilityProvider>
                  </AbilityProvider>
                </WarehouseProvider>
              </ThemeProvider>

              <ThemeProvider theme="human-theme">
                <AbilityProvider ability={{ entity_type: 'employee' }}>
                  <AbilityProvider ability={{ title: 'viewAny employee' }}>
                    <AuthRoute path={HUMAN_EMPLOYEES_INDEX}><Employees /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view employee' }}>
                    <AuthRoute path={HUMAN_EMPLOYEES_VIEW}><ViewEmployee /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'create employee' }}>
                    <AuthRoute path={HUMAN_EMPLOYEES_CREATE}><CreateEmployee /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update employee' }}>
                    <AuthRoute path={HUMAN_EMPLOYEES_EDIT}><EditEmployee /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>

                <AbilityProvider ability={{ entity_type: 'partner' }}>
                  <AuthRoute path={HUMAN_PARTNERS_INDEX}><Partners /></AuthRoute>
                  <AuthRoute path={HUMAN_PARTNERS_CREATE}><CreatePartner /></AuthRoute>
                  <AuthRoute path={HUMAN_PARTNERS_VIEW}><ViewPartner /></AuthRoute>
                  <AuthRoute path={HUMAN_PARTNERS_EDIT}><EditPartner /></AuthRoute>
                  <AuthRoute path={HUMAN_PARTNER_DEPARTMENTS_CREATE}><CreatePartnerDepartment /></AuthRoute>
                  <AuthRoute path={HUMAN_PARTNER_DEPARTMENTS_EDIT}><EditPartnerDepartment /></AuthRoute>
                </AbilityProvider>
              </ThemeProvider>

              <ThemeProvider theme='documents-theme'>
                <AuthRoute path={DOCUMENT_STORAGE_INDEX}>
                  <DocumentStorage />
                </AuthRoute>
              </ThemeProvider>

              <ThemeProvider theme="finance-theme">
                <AuthRoute path={FINANCE_ANALYSIS_INDEX}><ProductionCostAnalysis /></AuthRoute>
                <AbilityProvider ability={{ entity_type: 'budget' }}>
                  <AuthRoute path={FINANCE_BUDGET_INDEX}><Budget /></AuthRoute>
                </AbilityProvider>
                <AuthRoute path={FINANCE_MODELING_INDEX}><ProductionModeling /></AuthRoute>
              </ThemeProvider>

              <ThemeProvider theme="equipment-theme">
                <AbilityProvider ability={{ entity_type: 'equipment' }}>
                  <AbilityProvider ability={{ title: 'viewAny equipment' }}>
                    <AuthRoute path={EQUIPMENT_LIST_INDEX}><EquipmentList /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view equipment' }}>
                    <AuthRoute path={EQUIPMENT_LIST_VIEW}><EquipmentView /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'create equipment' }}>
                    <AuthRoute path={EQUIPMENT_LIST_CREATE}><EquipmentCreate /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update equipment' }}>
                    <AuthRoute path={EQUIPMENT_LIST_EDIT}><EquipmentEdit /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>

                <AbilityProvider ability={{ entity_type: 'maintenance' }}>
                  <AbilityProvider ability={{ title: 'viewAny maintenance' }}>
                    <AuthRoute path={EQUIPMENT_MAINTENANCE_INDEX}><MaintenanceAndRepairs /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'view maintenance' }}>
                    <AuthRoute path={EQUIPMENT_MAINTENANCE_VIEW}><ViewMaintenance /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'create maintenance' }}>
                    <AuthRoute path={EQUIPMENT_MAINTENANCE_CREATE}><CreateMaintenance /></AuthRoute>
                  </AbilityProvider>
                  <AbilityProvider ability={{ title: 'update maintenance' }}>
                    <AuthRoute path={EQUIPMENT_MAINTENANCE_EDIT}><EditMaintenance /></AuthRoute>
                  </AbilityProvider>
                </AbilityProvider>
              </ThemeProvider>

              <ThemeProvider theme="reports-theme">
                <AbilityProvider ability={{ entity_type: 'biological_farming' }}>
                  <AuthRoute path={REPORTING_BIOLOGICAL_FARMING_INDEX}><BiologicalFarmingReport /></AuthRoute>
                </AbilityProvider>
                <AbilityProvider ability={{ title: 'viewAny vaad_liz_reports' }}>
                  <AuthRoute path={REPORTING_VAAD_LIZ_REPORTS}><VaadLizReportV2List /></AuthRoute>
                  <AuthRoute path={REPORTING_VAAD_LIZ_REPORTS_CREATE}><CreateReport/></AuthRoute>
                  <AuthRoute path={REPORTING_VAAD_LIZ_REPORTS_UPDATE}><EditReport/></AuthRoute>
                  <AuthRoute path={REPORTING_VAAD_LIZ_REPORTS_VIEW}><ViewReport/></AuthRoute>
                </AbilityProvider>
                <AbilityProvider ability={{ title: 'viewAny report_journal' }}>
                  <AuthRoute path={REPORTING_REPORT_JOURNALS_INDEX}><ReportJournalsList /></AuthRoute>
                </AbilityProvider>
              </ThemeProvider>
            </UnauthorizedRedirectProvider>
          </RedirectProvider>
        </GuardProvider>
      </LayoutProvider>

      {/* Internal routes */}
      <GuestRoute path={INTERNAL_LOGIN} redirectTo={INTERNAL_DASHBOARD} guard={'ldap'} otherRedirectTo={HOMEPAGE}>
        <InternalLogin />
      </GuestRoute>
      <LayoutProvider
        layout={({ children }: { children: ReactNode }) => (
          <Layout
            sidebar={<InternalSidebar />}
            topbar={<InternalTopbar />}
            children={children}
          />
        )}
      >
        <GuardProvider guard={'ldap'}>
          <RedirectProvider redirect={INTERNAL_LOGIN}>
            <AuthRoute path={INTERNAL_DASHBOARD}><InternalDashboard /></AuthRoute>

            <AbilityProvider ability={{ entity_type: 'user' }}>
              <AuthRoute path={INTERNAL_USERS_INDEX}><IndexUser /></AuthRoute>
              <AuthRoute path={INTERNAL_USERS_CREATE}><CreateUser /></AuthRoute>
              <AuthRoute path={INTERNAL_USERS_VIEW}><ViewUser /></AuthRoute>
              <AuthRoute path={INTERNAL_USERS_EDIT}><EditUser /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'role' }}>
              <AuthRoute path={INTERNAL_ROLES_INDEX}><IndexRole /></AuthRoute>
              <AuthRoute path={INTERNAL_ROLES_CREATE}><CreateRole /></AuthRoute>
              <AuthRoute path={INTERNAL_ROLES_VIEW}><ViewRole /></AuthRoute>
              <AuthRoute path={INTERNAL_ROLES_EDIT}><EditRole /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'article' }}>
              <AuthRoute path={INTERNAL_ARTICLES_INDEX}><Articles /></AuthRoute>
              <AuthRoute path={INTERNAL_ARTICLES_CREATE}><CreateArticle /></AuthRoute>
              <AuthRoute path={INTERNAL_ARTICLES_VIEW}><ViewArticle /></AuthRoute>
              <AuthRoute path={INTERNAL_ARTICLES_EDIT}><EditArticle /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'event' }}>
              <AuthRoute path={INTERNAL_EVENTS_INDEX}><Events /></AuthRoute>
              <AuthRoute path={INTERNAL_EVENTS_CREATE}><CreateEvent /></AuthRoute>
              <AuthRoute path={INTERNAL_EVENTS_VIEW}><ViewEvent /></AuthRoute>
              <AuthRoute path={INTERNAL_EVENTS_EDIT}><EditEvent /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={(params: any) => ({ name: `Manage ${params.groupKey} classifiers` })}>
              <AuthRoute path={INTERNAL_CLASSIFIERS_INDEX}><Classifiers /></AuthRoute>
              <AuthRoute path={INTERNAL_CLASSIFIER_VALUES}><ClassifierValues /></AuthRoute>
              <AuthRoute path={INTERNAL_CLASSIFIER_VALUE_CREATE}><CreateClassifierValue /></AuthRoute>
              <AuthRoute path={INTERNAL_CLASSIFIER_VALUE_VIEW}><ViewClassifierValue /></AuthRoute>
              <AuthRoute path={INTERNAL_CLASSIFIER_VALUE_EDIT}><EditClassifierValue /></AuthRoute>
              <AuthRoute path={INTERNAL_CLASSIFIER_VALUE_CHILD_CREATE}><CreateClassifierValue /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'email_template' }}>
              <AuthRoute path={INTERNAL_EMAIL_TEMPLATE_INDEX}><IndexEmailTemplate /></AuthRoute>
              <AuthRoute path={INTERNAL_EMAIL_TEMPLATE_EDIT}><EditEmailTemplate /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'system_configuration' }}>
              <AuthRoute path={INTERNAL_SYSTEM_PROPERTIES_INDEX}><IndexSystemConfiguration /></AuthRoute>
              <AuthRoute path={INTERNAL_SYSTEM_PROPERTIES_EDIT}><EditSystemConfiguration /></AuthRoute>
            </AbilityProvider>

            <AbilityProvider ability={{ entity_type: 'audit' }}>
              <AuthRoute path={INTERNAL_AUDIT_LOGS_INDEX}><IndexAuditLogs /></AuthRoute>
              <AuthRoute path={INTERNAL_AUDIT_LOGS_VIEW}><ViewAuditLog /></AuthRoute>
            </AbilityProvider>

            <DevelopmentSandboxRoute />
          </RedirectProvider>
        </GuardProvider>
      </LayoutProvider>

      <RedirectIfNoMatch to={HOMEPAGE} />
    </BrowserRouter>
  );
}

function GuestRoute({
  children,
  redirectTo,
  exact = true,
  otherRedirectTo,
  guard,
  path,
  ...rest
}: {
  children: ReactNode;
  redirectTo?: string;
  guard?: string;
  otherRedirectTo?: string;
  exact?: boolean;
  path: string;
}) {
  const { user, userGuard } = useSelector((state: RootState) => ({
    user: state.global.user,
    userGuard: state.global.guard,
  }));

  const guardContext = useGuard();
  const routeGuard = guard || guardContext;

  const redirectContext = useRedirect();
  let routeRedirect = redirectTo || redirectContext;

  const otherRedirectContext = useOtherRedirect();
  if (
    user &&
    routeGuard !== userGuard &&
    (otherRedirectTo || otherRedirectContext)
  ) {
    routeRedirect = otherRedirectTo || otherRedirectContext;
  }

  const Layout = useLayout();

  return (
    <Route
      exact={exact}
      path={path.split('?')[0]}
      {...rest}
      render={({ location }) => {
        return !user ? (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            {SeoTags(path)}
            {Layout ? <Layout children={children} /> : children}
          </ErrorBoundary>
        ) : (
          <Redirect
            to={{
              pathname:
                (location.state as any)?.from?.pathname || routeRedirect,
              search: (location.state as any)?.from?.search,
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
}

function AuthRoute({
  guard,
  children,
  redirectTo,
  ability,
  path,
  exact = true,
  ...rest
}: {
  children: ReactNode;
  redirectTo?: string;
  guard?: string;
  exact?: boolean;
  path: string;
  ability?: Partial<Ability>;
}) {
  const { farmUser, userGuard } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
    userGuard: state.global.guard,
  }));
  const history = useHistory();

  const matched = useRouteMatch({ path: path.split('?')[0], exact: true });
  if (matched) {
    const indexRoutes = Object.entries(ROUTES).filter(([key]) =>
      key.includes('INDEX')
    );
    const isIndexRoute =
      indexRoutes.filter((route) => route[1] === matched.path).length > 0;
    const lastPage = sessionStorage.getItem('last_page');

    if (isIndexRoute) {
      if (lastPage !== matched.url) {
        const search = JSON.parse(sessionStorage.getItem('current_index_page'));
        if (search?.key === matched.url) {
          history.replace({ ...history.location, search: search.search });
          sessionStorage.setItem(
            'current_index_page',
            JSON.stringify({
              key: matched.url,
              search: history.location.search,
            })
          );
        }
      } else {
        sessionStorage.setItem(
          'current_index_page',
          JSON.stringify({ key: matched.url, search: history.location.search })
        );
      }
    }
    sessionStorage.setItem('last_page', matched.url);
  }

  const Layout = useLayout();
  const guardContext = useGuard();
  const routeGuard = guard || guardContext;

  const redirectContext = useRedirect();
  const unauthorizedRedirectContext = useUnauthorizedRedirect();
  let routeRedirect = redirectTo || redirectContext;

  const abilityContext = useAbility();
  const abilityCheck = can(
    farmUser,
    ability || abilityContext,
    matched?.params
  );

  if (!abilityCheck && unauthorizedRedirectContext) {
    routeRedirect = unauthorizedRedirectContext;
  }

  return (
    <Route
      exact={exact}
      path={path.split('?')[0]}
      {...rest}
      render={({ location }) =>
        routeGuard === userGuard && farmUser && abilityCheck ? (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            {SeoTags(path)}
            {Layout ? <Layout children={children} /> : children}
          </ErrorBoundary>
        ) : (
          <Redirect
            to={{
              pathname: routeRedirect,
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

export function SeoTags(routePath: string) {
  const pathKey = _.invert(ROUTES)[routePath];
  const transKey = 'seo.'.concat(pathKey, '.title');
  const titleStart = i18n.t('MansLauks_'.concat(process.env.NODE_ENV));
  return (
    <HelmetProvider>
      <Helmet>
        <title>
          {i18n.exists(transKey)
            ? titleStart.concat(' - ', i18n.t(transKey))
            : titleStart}
        </title>
      </Helmet>
    </HelmetProvider>
  );
}

/**
 * Route for the development sandbox, will only be available in development mode (locally).
 */
function DevelopmentSandboxRoute() {
  const Layout = useLayout();
  const sandboxLayout = <Layout children={<DevelopmentSandbox />} />;
  return (
    <VisibleInDevelopment>
      <Route path={INTERNAL_DEVELOPMENT_SANDBOX}>{sandboxLayout}</Route>
    </VisibleInDevelopment>
  );
}

function RedirectIfNoMatch({ to }: { to: string }) {
  const matched = useRouteMatch({
    path: Object.values(ROUTES).map((v) => v.split('?')[0]),
    exact: true,
  });

  if (!matched) {
    return <Redirect to={to} />;
  }

  return null;
}
